import React from "react";
import {
  Box,
  Paper,
  Text,
  Group,
  ThemeIcon,
  Title,
  Stack,
  SimpleGrid,
  Divider,
  Progress,
  Badge,
  Center,
  LoadingOverlay,
} from "@mantine/core";
import { IconCurrencyRupee, IconReceipt2 } from "@tabler/icons-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  LabelList,
} from "recharts";

function EmptyChartState({ text }) {
  return (
    <Center h={200}>
      <Text size="sm" c="dimmed" ta="center" maw={260}>
        {text}
      </Text>
    </Center>
  );
}

const EMPTY_INSIGHTS = {
  totalRevenue: 0,
  totalSales: 0,
  hourlyTraffic: [],
  demographics: [],
  funnel: [],
  visitorBreakdown: [],
};

/**
 * Shared shop-analytics UI, backed by GET .../shops/:shopId/analytics
 * (see Ota-BE Connectors/CrowdFunding/shopAnalyticsHelper.js for the query
 * logic). Used by both the shop-owner's own workspace and the admin shop
 * workshop, since both need the exact same charts for a single shop.
 */
export default function ShopAnalyticsPanel({
  stats = EMPTY_INSIGHTS,
  loading = false,
  subtitle = "Real-time metrics for this specific storefront.",
}) {
  const s = { ...EMPTY_INSIGHTS, ...stats };

  return (
    <Box pos="relative" mih={400}>
      <LoadingOverlay
        visible={loading}
        zIndex={1000}
        overlayProps={{ radius: "sm", blur: 2 }}
      />

      <Stack gap="xl">
        {/* --- Top Level KPI Cards --- */}
        <Box>
          <Title order={3} size="h2">
            Store Performance
          </Title>
          <Text size="lg" c="dimmed">
            {subtitle}
          </Text>
        </Box>
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
          <Paper
            withBorder
            p="xl"
            radius="md"
            bg="blue.0"
            style={{ borderColor: "var(--mantine-color-blue-3)" }}
          >
            <Group justify="space-between" align="flex-start">
              <Box>
                <Text c="blue.8" size="sm" tt="uppercase" fw={700} mb={4}>
                  Gross Revenue
                </Text>
                <Text
                  fw={900}
                  size="h1"
                  c="blue.9"
                  style={{ fontSize: "2.5rem" }}
                >
                  ₹{s.totalRevenue.toLocaleString("en-IN")}
                </Text>
              </Box>
              <ThemeIcon color="blue.6" variant="light" size={64} radius="md">
                <IconCurrencyRupee size={32} />
              </ThemeIcon>
            </Group>
          </Paper>
          <Paper
            withBorder
            p="xl"
            radius="md"
            bg="teal.0"
            style={{ borderColor: "var(--mantine-color-teal-3)" }}
          >
            <Group justify="space-between" align="flex-start">
              <Box>
                <Text c="teal.8" size="sm" tt="uppercase" fw={700} mb={4}>
                  Total Orders
                </Text>
                <Text
                  fw={900}
                  size="h1"
                  c="teal.9"
                  style={{ fontSize: "2.5rem" }}
                >
                  {s.totalSales.toLocaleString()}
                </Text>
              </Box>
              <ThemeIcon color="teal.6" variant="light" size={64} radius="md">
                <IconReceipt2 size={32} />
              </ThemeIcon>
            </Group>
          </Paper>
        </SimpleGrid>

        <Divider my="sm" />

        {/* --- Detailed Data Charts --- */}
        <Box>
          <Title order={3} size="h2" mb="xs">
            Visitor Insights
          </Title>
          <Text size="lg" c="dimmed" mb="lg">
            Demographics and engagement patterns based on QR scans.
          </Text>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
            {/* 1. Visitor Demographics */}
            <Paper withBorder p="xl" radius="md" bg="white">
              <Title order={4} size="h3" mb="xs">
                Who scanned your booth
              </Title>
              <Text size="sm" c="dimmed" mb="xl">
                Age and gender breakdown of everyone who scanned your QR.
              </Text>
              {s.demographics.length === 0 ? (
                <EmptyChartState text="No scans yet — demographics show up once shoppers scan your QR code." />
              ) : (
                <Box h={280}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={s.demographics}
                        innerRadius={80}
                        outerRadius={110}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {s.demographics.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `${value}%`} />
                      <Legend
                        verticalAlign="bottom"
                        height={36}
                        iconType="circle"
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Paper>

            {/* 2. Traffic by Hour */}
            <Paper withBorder p="xl" radius="md" bg="white">
              <Title order={4} size="h3" mb="xs">
                When they showed up
              </Title>
              <Text size="sm" c="dimmed" mb="xl">
                Shows your busiest hours, so you know when to keep extra stock.
              </Text>
              {s.hourlyTraffic.length === 0 ? (
                <EmptyChartState text="No scans yet — traffic patterns show up once shoppers scan your QR code." />
              ) : (
                <Box h={280}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={s.hourlyTraffic}
                      margin={{ top: 20, right: 10, left: -20, bottom: 0 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#eee"
                      />
                      <XAxis
                        dataKey="time"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#888", fontSize: 12 }}
                        dy={10}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#888", fontSize: 12 }}
                      />
                      <Tooltip cursor={{ fill: "#f5f5f5" }} />
                      <Bar
                        dataKey="scans"
                        fill="#3b66e0"
                        radius={[4, 4, 0, 0]}
                        maxBarSize={40}
                      >
                        <LabelList
                          dataKey="scans"
                          position="top"
                          style={{ fontWeight: 700, fill: "#444", fontSize: 12 }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Paper>

            {/* 3. Conversion Funnel */}
            <Paper withBorder p="xl" radius="md" bg="white">
              <Title order={4} size="h3" mb="xs">
                From browser to customer
              </Title>
              <Text size="sm" c="dimmed" mb="xl">
                Shows how many lookers actually turn into real, engaged
                customers.
              </Text>
              {!s.funnel[0]?.val ? (
                <EmptyChartState text="No scans yet — your funnel fills in once shoppers scan your QR code." />
              ) : (
                <Stack gap="lg">
                  {s.funnel.map((item, index) => {
                    const percentage = Math.round(
                      (item.val / s.funnel[0].val) * 100,
                    );
                    return (
                      <Box key={index}>
                        <Group justify="space-between" mb={8} wrap="nowrap">
                          <Text size="md" fw={600} style={{ flex: 1 }}>
                            {item.stage}
                          </Text>
                          <Group gap="sm">
                            <Badge size="lg" variant="filled" color="blue.7">
                              {item.val}
                            </Badge>
                            <Text size="sm" c="dimmed" w={40} ta="right">
                              {percentage}%
                            </Text>
                          </Group>
                        </Group>
                        <Progress
                          value={percentage}
                          size="xl"
                          color="blue.5"
                          radius="md"
                        />
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Paper>

            {/* 4. Visitor Breakdown */}
            <Paper withBorder p="xl" radius="md" bg="white">
              <Title order={4} size="h3" mb="xs">
                New vs. returning vs. converted
              </Title>
              <Text size="sm" c="dimmed" mb="xl">
                Shows how many scanners are first-timers, coming back, or have
                already bought something.
              </Text>
              {s.visitorBreakdown.every((item) => item.val === 0) ? (
                <EmptyChartState text="No scans yet — this breaks down once shoppers scan your QR code." />
              ) : (
                <Stack gap="xl" justify="center" h="100%">
                  {s.visitorBreakdown.map((item, index) => {
                    const maxVal = Math.max(
                      1,
                      ...s.visitorBreakdown.map((v) => v.val),
                    );
                    return (
                      <Box key={index}>
                        <Group justify="space-between" mb={8}>
                          <Text size="md" fw={600}>
                            {item.name}
                          </Text>
                          <Text fw={800} size="lg">
                            {item.val}
                          </Text>
                        </Group>
                        <Progress
                          value={(item.val / maxVal) * 100}
                          color={item.color}
                          size="xl"
                          radius="md"
                        />
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Paper>
          </SimpleGrid>
        </Box>
      </Stack>
    </Box>
  );
}
